/**
 * Inline "Where can we ship?" badge shown next to the checkout button.
 *
 * - Pulls /api/pharmacies/shipping/coverage on mount + every 30 s, so any
 *   admin change to a pharmacy's ships_to_states list is reflected within
 *   half a minute without a page reload.
 * - Compares the patient's saved address_state against the aggregated
 *   union of all active pharmacy partners.
 * - When covered: small emerald pill, no fanfare.
 * - When NOT covered: red banner with the patient's state called out + the
 *   bound `onUnsupportedChange` hook so the parent can disable the Pay
 *   button (we don't disable directly from here to keep the component
 *   reusable in non-checkout surfaces).
 */
import { useEffect, useState } from "react";
import API from "@/lib/api";
import { CheckCircle2, AlertTriangle, Loader2, Mail } from "lucide-react";
import { toast } from "sonner";

const POLL_MS = 30_000;

export default function ShippingCoverageBadge({ patientState, patientEmail, onUnsupportedChange }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  // Waitlist UX state — only used when stateCode is NOT covered.
  const [waitlistEmail, setWaitlistEmail] = useState(patientEmail || "");
  const [waitlistSubmitting, setWaitlistSubmitting] = useState(false);
  const [waitlistJoined, setWaitlistJoined] = useState(false);

  useEffect(() => { if (patientEmail && !waitlistEmail) setWaitlistEmail(patientEmail); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [patientEmail]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { data: d } = await API.get("/pharmacies/shipping/coverage");
        if (!cancelled) setData(d);
      } catch {
        /* leave previous data in place */
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    const t = setInterval(load, POLL_MS);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  const stateCode = (patientState || "").trim().toUpperCase().slice(0, 2);
  const states = data?.states || [];
  const covered = stateCode && states.includes(stateCode);

  // Lift the "unsupported" verdict so the parent can gate its Pay button.
  // Wait until we actually have data + a patient state to make a decision —
  // never block prematurely on the loading flicker.
  useEffect(() => {
    if (loading || !data || !stateCode) return;
    if (typeof onUnsupportedChange === "function") {
      onUnsupportedChange(!covered);
    }
  }, [loading, covered, stateCode, data, onUnsupportedChange]);

  if (loading && !data) {
    return (
      <div className="inline-flex items-center gap-1.5 text-xs text-slate-400" data-testid="ship-coverage-loading">
        <Loader2 className="w-3 h-3 animate-spin" /> Checking shipping coverage…
      </div>
    );
  }
  if (!stateCode) {
    return (
      <div
        className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border border-amber-300 bg-amber-50 text-amber-800"
        data-testid="ship-coverage-no-state"
        title="Add a shipping state on your profile so we can confirm we can ship to you."
      >
        <AlertTriangle className="w-3 h-3" /> Add a shipping address to confirm coverage
      </div>
    );
  }
  if (covered) {
    return (
      <div
        className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border border-emerald-200 bg-emerald-50 text-emerald-700"
        data-testid="ship-coverage-ok"
        title={`Our partner pharmacies are licensed in ${states.length} states including ${stateCode}.`}
      >
        <CheckCircle2 className="w-3.5 h-3.5" /> We ship to {stateCode} · {states.length} states covered
      </div>
    );
  }
  return (
    <div
      className="rounded-xl border border-rose-300 bg-rose-50 dark:bg-rose-950/30 dark:border-rose-700/60 p-3"
      data-testid="ship-coverage-blocked"
    >
      <div className="flex items-start gap-2">
        <AlertTriangle className="w-4 h-4 text-rose-600 mt-0.5 shrink-0" />
        <div className="flex-1">
          <p className="text-sm font-semibold text-rose-900 dark:text-rose-100">
            We can&apos;t ship to <span className="underline">{stateCode}</span> yet
          </p>
          <p className="text-xs text-rose-800 dark:text-rose-200 mt-0.5">
            None of our partner pharmacies are licensed to ship to {stateCode} right now.
            Your order can&apos;t be fulfilled — please update your shipping state on your profile,
            or join the waitlist below and we&apos;ll email you the moment {stateCode} comes online.
          </p>
          <p className="text-[11px] text-rose-700 dark:text-rose-300 mt-1.5 opacity-80">
            Currently licensed in: <span className="font-mono">{states.slice(0, 12).join(", ")}{states.length > 12 ? ` +${states.length - 12} more` : ""}</span>
          </p>

          {/* Waitlist signup — anonymous OK; we capture state + email and
              auto-notify the moment any pharmacy partner adds this state. */}
          {waitlistJoined ? (
            <div className="mt-3 inline-flex items-center gap-1.5 text-xs text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800 px-2.5 py-1 rounded-full" data-testid="ship-coverage-waitlist-joined">
              <CheckCircle2 className="w-3.5 h-3.5" /> You&apos;re on the list — we&apos;ll email {waitlistEmail} when {stateCode} ships.
            </div>
          ) : (
            <form
              className="mt-3 flex flex-col sm:flex-row gap-2"
              onSubmit={async (e) => {
                e.preventDefault();
                const v = (waitlistEmail || "").trim().toLowerCase();
                if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v)) {
                  toast.error("Enter a valid email"); return;
                }
                setWaitlistSubmitting(true);
                try {
                  await API.post("/pharmacies/shipping/waitlist", { email: v, state: stateCode, source: "checkout" });
                  setWaitlistJoined(true);
                  toast.success(`On the list! We'll email you when ${stateCode} comes online.`);
                } catch {
                  toast.error("Couldn't add you to the waitlist — try again in a moment.");
                } finally {
                  setWaitlistSubmitting(false);
                }
              }}
              data-testid="ship-coverage-waitlist-form"
            >
              <div className="flex-1 flex items-center gap-1.5 bg-white dark:bg-slate-900 border border-rose-200 dark:border-rose-800 rounded-lg px-2.5">
                <Mail className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                <input
                  type="email"
                  value={waitlistEmail}
                  onChange={(e) => setWaitlistEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="flex-1 text-sm bg-transparent py-2 focus:outline-none"
                  data-testid="ship-coverage-waitlist-email"
                />
              </div>
              <button
                type="submit"
                disabled={waitlistSubmitting}
                className="rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-xs font-semibold px-4 py-2 disabled:opacity-50"
                data-testid="ship-coverage-waitlist-submit"
              >
                {waitlistSubmitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Notify me"}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
